import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, HStack } from "@chakra-ui/layout";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Checkbox } from "@chakra-ui/checkbox";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Button, SkeletonText } from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ApplicationsTable } from "../../components/Applications/ApplicationsTable";
import { useSearchInput } from "../../hooks/useSearchInput";

export const Applications = () => {
  const apiClient = useAdminApiClient();
  const navigate = useNavigate();
  const {
    checked: showDeletedApplications,
    onChange: onChangeDeletedApplicationCheck,
  } = useCheckboxState(false);

  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search application...",
  });

  const {
    items: applications,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getPlatformApplications({
        q: debouncedQuery,
        includeDeleted: showDeletedApplications ? true : false,
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getPlatformApplications({
        q: debouncedQuery,
        includeDeleted: showDeletedApplications ? true : false,
        continuationToken,
      }),
    [debouncedQuery, showDeletedApplications]
  );

  const deleteApplicationRequest = useAdminApiUpdateRequest(
    (apiClient, applicationId: string) =>
      apiClient.deletePlatformApplication(applicationId),
    [applications]
  );

  const restoreApplicationRequest = useAdminApiUpdateRequest(
    (apiClient, applicationId: string) =>
      apiClient.restorePlatformApplication(applicationId),
    [applications]
  );

  const onClickNewApplication = () => navigate(`/admin/applications/new`);

  const onClickEditApplication = (
    application: CherryHubAdminApi.PlatformApplicationSummaryItem
  ) => navigate(`/admin/applications/${application.Meta?.Id}`);

  useEffect(() => {
    if (deleteApplicationRequest.result) {
      if (deleteApplicationRequest.result.ok) {
        showSuccessToast("Application deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteApplicationRequest.result.message ??
            "Failed to delete application."
        );
      }
    }
  }, [deleteApplicationRequest.result, refresh]);

  const onClickDeleteApplication = useCallback(
    (application: CherryHubAdminApi.PlatformApplicationSummaryItem) =>
      showConfirmationDialog(
        "Delete Application?",
        `Are you sure you wish to delete this application: ${application.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteApplicationRequest.request(application.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  useEffect(() => {
    if (restoreApplicationRequest.result) {
      if (restoreApplicationRequest.result.ok) {
        showSuccessToast("Deleted application restored.");
        refresh();
      } else {
        showErrorToast(
          restoreApplicationRequest.result.message ??
            "Failed to restore deleted application."
        );
      }
    }
  }, [restoreApplicationRequest.result, refresh]);

  const onClickRestoreApplication = useCallback(
    (application: CherryHubAdminApi.PlatformApplicationSummaryItem) =>
      showConfirmationDialog(
        "Restore Application?",
        `Are you sure you wish to restore this deleted application: ${application.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreApplicationRequest.request(application.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Applications</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <HStack w="100%">
          <Box flex="4" maxWidth="400px">
            {searchInputElement}
          </Box>
          <Box flex="1">
            <Checkbox
              checked={showDeletedApplications}
              onChange={onChangeDeletedApplicationCheck}
              title="Include deleted application in the list."
            >
              Include Deleted
            </Checkbox>
          </Box>
          <Box marginLeft="auto">
            <Button
              leftIcon={<PlusIcon />}
              onClick={onClickNewApplication}
              colorScheme="cherryButton"
            >
              New Application
            </Button>
          </Box>
        </HStack>

        <ApplicationsTable
          applications={applications}
          isLoading={isLoading}
          disableActions={deleteApplicationRequest.isLoading}
          debouncedQuery={debouncedQuery}
          onClickEditApplication={onClickEditApplication}
          onClickDeleteApplication={onClickDeleteApplication}
          onClickRestoreApplication={onClickRestoreApplication}
        />

        {fetchNextPage && !isLoading && (
          <Box w="100%">
            {!isLoadingPage && (
              <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                Load more...
              </Button>
            )}
            {isLoadingPage && (
              <HStack width="100%" boxShadow="lg">
                <Box width="25%" pt="4" pb="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="65%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4" pr="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
              </HStack>
            )}
          </Box>
        )}
        {error != null && (
          <ErrorMessage>
            {error ?? "An error was encountered while searching applications."}
          </ErrorMessage>
        )}
        {(!applications || isLoading) && <LoadingIndicator />}
        {confirmationElement}
      </PageContent>
    </>
  );
};
